import { EDoorWindowSide } from '@/editor/data/model/model';
import { IndicatorEntity } from '@/editor/entities/indicatorEntity';
import { EIndicatorSide, IIndicatorEntityParams } from '@/editor/entities/type';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { modelManager } from '.';
import i18n from '@/i18n';

export class IndicatorManager {
  private _id?: string;
  private _visible: boolean = false;
  private _entity: IndicatorEntity;
  private _params: IIndicatorEntityParams | null = null;

  constructor() {
    this._entity = new IndicatorEntity();

    editorEvent.on(EditorEventName.RESIZE, this.handleResize);
    editorEvent.on(EditorEventName.VIEW2D_CHANGE, this.handleResize);
  }

  setVisible(visible: boolean) {
    this._visible = visible;
    if (!visible) {
      this._entity.clear();
    }
    this._update(true);
  }

  setDoorIndicator(doorId: string) {
    this._id = doorId;
    const doorData = globalStores.modelStore.getWindowData(doorId);

    if (!doorData) {
      this._entity.clear();
      this._params = null;
      return;
    }

    const relateId = doorData.relativeWallInfo.relativeWallId;
    const wallData = globalStores.wallStore.getWallData(relateId);
    const separateWallData = globalStores.separateWallStore.getSepData(relateId, false);

    let start: IPoint2d = { x: 0, y: 0 };
    let end: IPoint2d = { x: 0, y: 0 };
    let thickness: number = 0;

    if (wallData) {
      start = wallData.baseData.start;
      end = wallData.baseData.end;
      thickness = wallData.baseData.thickness;
    }

    if (separateWallData) {
      start = separateWallData.start;
      end = separateWallData.end;
      thickness = separateWallData.thickness;
    }

    const angle = Point2dUtils.getAngle(start, end);
    const direction = Point2dUtils.getDirection(start, end);

    let nextSide = EIndicatorSide.down;
    const isFlipped = doorData.side === EDoorWindowSide.left;

    const verticalThreshold = Math.PI / 6;
    const horizontalThreshold = Math.PI - verticalThreshold;

    if (angle >= -verticalThreshold && angle <= verticalThreshold) {
      nextSide = isFlipped ? EIndicatorSide.up : EIndicatorSide.down;
    } else if (angle >= horizontalThreshold || angle <= -horizontalThreshold) {
      nextSide = isFlipped ? EIndicatorSide.down : EIndicatorSide.up;
    } else if (angle > verticalThreshold && angle < horizontalThreshold) {
      nextSide = isFlipped ? EIndicatorSide.left : EIndicatorSide.right;
    } else {
      nextSide = isFlipped ? EIndicatorSide.right : EIndicatorSide.left;
    }

    if (doorData.side === EDoorWindowSide.left) {
      direction.x = -direction.x;
      direction.y = -direction.y;
    }

    const nextCenter = Point2dUtils.addDistance(doorData.center, thickness / 2, {
      x: -direction.y,
      y: direction.x
    });

    this._params = {
      center: nextCenter,
      label: i18n.t('draw.layout.tool.entrance'),
      side: nextSide
    };

    this._update(true);
  }

  private handleResize = () => {
    if (this._visible) {
      this._update();
    }
  };

  private _update(forceUpdate = false) {
    requestAnimationFrame(() => {
      if (this._visible && this._params) {
        this._entity.update({ ...this._params, forceUpdate });
        if (forceUpdate) {
          modelManager.windowManager.entities.forEach((entity) => {
            entity.setIsEntrance(entity.id === this._id);
          });
        }
      } else {
        this._entity.clear();
        modelManager.windowManager.entities.forEach((entity) => {
          entity.setIsEntrance(false);
        });
      }
    });
  }

  dispose() {
    editorEvent.off(EditorEventName.RESIZE, this.handleResize);
    editorEvent.off(EditorEventName.VIEW2D_CHANGE, this.handleResize);
  }
}
